import { Color, DynamicDrawUsage, InstancedBufferAttribute, InstancedBufferGeometry, Mesh, PlaneGeometry, type Object3D, type ShaderMaterial } from "three";
import { createSmokeMaterial, FLAME_GLOW, FLAME_GLOW_REDUCED } from "../render/shaders/smoke.ts";

/** Fumaça e, desde a Fase 14, a poeira dos pés (a Investida de três corpos não expulsa a fumaça de quem se desfaz). */
export const SMOKE_POOL = 160;
/** Teto das chamas e da magia juntas (F93), à parte: uma chuva de fogo não apaga a fumaça de quem se desfaz. */
export const GLOW_POOL = 160;
const LIFE = 1.6;
/** O tipo de cada baforada no shader (`aKind`). */
export const PUFF_KIND = { fumaca: 0, chama: 1, magia: 2, poeira: 3, onda: 4, marca: 5 } as const;
/**
 * Ondas na água e marcas de lama dos passos, à parte (uma trilha longa no mangue não expulsa a fumaça), no
 * mesmo programa; o draw só fica ligado com alguma viva.
 */
export const MARK_POOL = 128;
/** Vida (s) de uma onda na água. */
export const RIPPLE_LIFE = 1.1;
/** Vida (s) de uma marca de lama (no Baixo, metade): fica, e some no último terço. */
export const MARK_LIFE = 8;
/**
 * A onda (linear): a espuma clara do mar. A marca: preta, então só escurece o chão pela opacidade (a lama
 * molhada); uma cor fixa sem luz saía clara no chão escuro do mangue ao entardecer.
 */
export const RIPPLE_COLOR = [0.82, 0.85, 0.86] as const;
export const MARK_COLOR = [0, 0, 0] as const;
/** O cinza da fumaça de sempre (linear, como estava no shader). */
export const SMOKE_GRAY = [0.42, 0.4, 0.46] as const;
/** A poeira dos pés (Fase 14, linear): um cinza quente e claro, o da névoa no chão. */
export const DUST_COLOR = [0.46, 0.41, 0.35] as const;
/** Vida (s) de um sopro de poeira (cada um varia até 30 % a menos). */
export const DUST_LIFE = 0.9;
/** Vida (s) de uma lambida de chama e de uma faísca de magia (cada uma varia até 30 % a menos). */
export const FLAME_LIFE = 0.8;
export const MAGIC_LIFE = 0.5;

/** Ângulo áureo: espalha sem aleatório (a mesma chamada, o mesmo desenho), como nos estilhaços. */
const GOLDEN = Math.PI * (3 - Math.sqrt(5));

/** Campos de cada baforada no bloco do pool. */
const X = 0;
const Y = 1;
const Z = 2;
const VX = 3;
const VY = 4;
const VZ = 5;
const AGE = 6;
const LIFE_AT = 7;
const SIZE = 8;
const ALPHA = 9;
const R = 10;
const G = 11;
const B = 12;
const KIND = 13;
const STRIDE = 14;

/**
 * Um pool de baforadas num draw: um bloco de floats por baforada, em ordem de nascimento (a mais antiga
 * primeiro, e é ela que cede o lugar com o pool cheio). O quadro só escreve por índice.
 */
class PuffPool {
  readonly mesh: Mesh;
  private readonly geometry: InstancedBufferGeometry;
  private readonly center: InstancedBufferAttribute;
  private readonly size: InstancedBufferAttribute;
  private readonly alpha: InstancedBufferAttribute;
  private readonly color: InstancedBufferAttribute;
  private readonly kind: InstancedBufferAttribute;
  private readonly data: Float64Array;
  readonly capacity: number;
  live = 0;

  constructor(root: Object3D, plane: PlaneGeometry, capacity: number, glow: boolean, renderOrder: number) {
    this.capacity = capacity;
    this.data = new Float64Array(capacity * STRIDE);
    this.geometry = new InstancedBufferGeometry();
    this.geometry.setIndex(plane.getIndex());
    this.geometry.setAttribute("position", plane.getAttribute("position"));
    this.geometry.setAttribute("uv", plane.getAttribute("uv"));
    const attr = (n: number) => new InstancedBufferAttribute(new Float32Array(capacity * n), n).setUsage(DynamicDrawUsage);
    this.center = attr(3);
    this.size = attr(1);
    this.alpha = attr(1);
    this.color = attr(3);
    this.kind = attr(1);
    this.geometry.setAttribute("aCenter", this.center);
    this.geometry.setAttribute("aSize", this.size);
    this.geometry.setAttribute("aAlpha", this.alpha);
    this.geometry.setAttribute("aColor", this.color);
    this.geometry.setAttribute("aKind", this.kind);
    this.geometry.instanceCount = 0;
    this.mesh = new Mesh(this.geometry, createSmokeMaterial(glow));
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
    root.add(this.mesh);
  }

  get material(): ShaderMaterial {
    return this.mesh.material as ShaderMaterial;
  }

  /** O bloco de uma baforada nova (com o pool cheio, a mais antiga sai). */
  push(): number {
    if (this.live >= this.capacity) {
      this.data.copyWithin(0, STRIDE, this.live * STRIDE);
      this.live--;
    }
    return this.live++ * STRIDE;
  }

  write(o: number, x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, size: number, alpha: number, r: number, g: number, b: number, kind: number): void {
    const d = this.data;
    d[o + X] = x;
    d[o + Y] = y;
    d[o + Z] = z;
    d[o + VX] = vx;
    d[o + VY] = vy;
    d[o + VZ] = vz;
    d[o + AGE] = 0;
    d[o + LIFE_AT] = life;
    d[o + SIZE] = size;
    d[o + ALPHA] = alpha;
    d[o + R] = r;
    d[o + G] = g;
    d[o + B] = b;
    d[o + KIND] = kind;
  }

  /** Anda, tira as que acabaram (sem mudar a ordem) e escreve os atributos. */
  update(dt: number): void {
    const d = this.data;
    let i = 0;
    for (let k = 0; k < this.live; k++) {
      const o = k * STRIDE;
      const age = d[o + AGE]! + dt;
      const life = d[o + LIFE_AT]!;
      if (age >= life) continue;
      const w = i * STRIDE;
      if (w !== o) d.copyWithin(w, o, o + STRIDE);
      d[w + AGE] = age;
      const kind = Math.floor(d[w + KIND]!);
      if (kind === PUFF_KIND.chama) d[w + VY] = d[w + VY]! + 1.6 * dt;
      else if (kind === PUFF_KIND.poeira) {
        // A poeira abre rente ao chão e para (arrasto forte), subindo devagar.
        const keep = Math.max(0, 1 - 3.5 * dt);
        d[w + VX] = d[w + VX]! * keep;
        d[w + VY] = d[w + VY]! * keep + 0.15 * dt;
        d[w + VZ] = d[w + VZ]! * keep;
      }
      else if (kind === PUFF_KIND.magia) {
        // A faísca abre e para (arrasto), flutuando um pouco.
        const keep = Math.max(0, 1 - 4 * dt);
        d[w + VX] = d[w + VX]! * keep;
        d[w + VY] = d[w + VY]! * keep + 0.4 * dt;
        d[w + VZ] = d[w + VZ]! * keep;
      }
      d[w + X] = d[w + X]! + d[w + VX]! * dt;
      d[w + Y] = d[w + Y]! + d[w + VY]! * dt;
      d[w + Z] = d[w + Z]! + d[w + VZ]! * dt;
      const t = age / life;
      this.center.setXYZ(i, d[w + X]!, d[w + Y]!, d[w + Z]!);
      this.color.setXYZ(i, d[w + R]!, d[w + G]!, d[w + B]!);
      this.kind.setX(i, d[w + KIND]!);
      if (kind === PUFF_KIND.fumaca) {
        // A fumaça de sempre: cresce de 0,6 a 2 m e some.
        this.size.setX(i, 0.6 + t * 1.4);
        this.alpha.setX(i, 0.55 * (1 - t));
      } else if (kind === PUFF_KIND.poeira) {
        // Abre logo (raiz do tempo) e some devagar, sem estalo na entrada.
        this.size.setX(i, d[w + SIZE]! * (0.45 + 1.1 * Math.sqrt(t)));
        this.alpha.setX(i, d[w + ALPHA]! * Math.min(1, t / 0.08) * (1 - t) ** 1.5);
      } else if (kind === PUFF_KIND.chama) {
        // A lambida acende rápido, fica cheia, cresce um pouco e encolhe no fim, sumindo.
        this.size.setX(i, d[w + SIZE]! * (0.75 + 0.5 * t) * (1 - 0.6 * t * t));
        this.alpha.setX(i, d[w + ALPHA]! * Math.min(1, t / 0.12) * (1 - t * t));
      } else if (kind === PUFF_KIND.onda) {
        // Abre rápido e devagar no fim (raiz do tempo), apagando.
        this.size.setX(i, d[w + SIZE]! * (0.25 + 0.75 * Math.sqrt(t)));
        this.alpha.setX(i, d[w + ALPHA]! * Math.min(1, t / 0.06) * (1 - t) ** 1.5);
      } else if (kind === PUFF_KIND.marca) {
        // Assenta (um quinto de segundo, de 80 % do tamanho), fica e some no último terço da vida.
        this.size.setX(i, d[w + SIZE]! * (0.8 + 0.2 * Math.min(1, age / 0.2)));
        this.alpha.setX(i, d[w + ALPHA]! * Math.min(1, 1.5 * (1 - t)));
      } else {
        // A faísca estoura grande e encolhe.
        this.size.setX(i, d[w + SIZE]! * (0.6 + 0.4 * Math.min(1, t * 6)) * (1 - 0.5 * t));
        this.alpha.setX(i, d[w + ALPHA]! * (1 - t) * (1 - t));
      }
      i++;
    }
    this.live = i;
    this.geometry.instanceCount = i;
    this.center.needsUpdate = true;
    this.size.needsUpdate = true;
    this.alpha.needsUpdate = true;
    this.color.needsUpdate = true;
    this.kind.needsUpdate = true;
  }

  /** Quantas de um tipo estão vivas (diagnóstico). */
  countKind(kind: number): number {
    let n = 0;
    for (let k = 0; k < this.live; k++) if (Math.floor(this.data[k * STRIDE + KIND]!) === kind) n++;
    return n;
  }

  clear(): void {
    this.live = 0;
    this.geometry.instanceCount = 0;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}

/**
 * Baforadas de fumaça que sobem, crescem e somem (dissolução das assombrações) e, desde a F93, as chamas e a
 * magia, no mesmo programa (`fumaca`): a fumaça num draw de mistura normal e as chamas e a magia num segundo,
 * pré-multiplicado, que fica invisível quando vazio (sem draw call parada). Determinístico pela ordem das chamadas
 * (sem `Math.random`), como os estilhaços.
 */
export class Smoke {
  private readonly smoke: PuffPool;
  private readonly glow: PuffPool;
  private readonly marks: PuffPool;
  private readonly plane = new PlaneGeometry(1, 1);
  private readonly colors = new Map<string, Color>();
  private seq = 0;
  private time = 0;
  private reduced = false;

  constructor(root: Object3D) {
    this.smoke = new PuffPool(root, this.plane, SMOKE_POOL, false, 9);
    this.glow = new PuffPool(root, this.plane, GLOW_POOL, true, 9);
    this.glow.mesh.visible = false;
    // Depois do mar (transparente, ordem 10, sem profundidade): senão ele pinta por cima das ondas. Na terra o
    // mar fica embaixo do chão e não cobre as marcas de lama.
    this.marks = new PuffPool(root, this.plane, MARK_POOL, false, 11);
    this.marks.mesh.visible = false;
    // Rente ao chão sem brigar com ele: a malha do terreno (2 m) não bate com a altura da simulação, e a
    // marca deitada piscava em retalhos. O deslocamento é estado do material, não muda o programa.
    const marks = this.marks.material;
    marks.polygonOffset = true;
    marks.polygonOffsetFactor = -4;
    marks.polygonOffsetUnits = -4;
  }

  /** Uma onda na água em (x, y, z) que abre até `size` m de largura (os passos no raso e nadando). */
  ripple(x: number, y: number, z: number, size = 1.2, alpha = 0.6): void {
    if (alpha <= 0) return;
    const [r, g, b] = RIPPLE_COLOR;
    const k = this.seq++;
    this.marks.write(this.marks.push(), x, y, z, 0, 0, 0, RIPPLE_LIFE, size, alpha, r, g, b, PUFF_KIND.onda + seed(k));
    this.marks.mesh.visible = true;
  }

  /** Uma marca de lama de `size` m no chão em (x, y, z), que fica `life` s (os passos no mangue e na areia molhada). */
  mudMark(x: number, y: number, z: number, size = 0.45, alpha = 0.55, life = MARK_LIFE): void {
    if (alpha <= 0 || life <= 0) return;
    const [r, g, b] = MARK_COLOR;
    const k = this.seq++;
    this.marks.write(this.marks.push(), x, y, z, 0, 0, 0, life, size, alpha, r, g, b, PUFF_KIND.marca + seed(k));
    this.marks.mesh.visible = true;
  }

  /**
   * "Reduzir lampejos" (F93): as chamas param de tremeluzir e a magia de cintilar, nenhum canal passa de 1
   * e as duas só cobrem o fundo, sem somar luz: o resultado fica entre a cor delas e a do fundo, então nada
   * passa do limiar do bloom que já não passava. A fumaça não muda.
   */
  set reduceFlashes(on: boolean) {
    this.reduced = on;
    const u = this.glow.material.uniforms;
    u["uFlicker"]!.value = on ? 0 : 1;
    u["uGlow"]!.value = on ? FLAME_GLOW_REDUCED : FLAME_GLOW;
    u["uOver"]!.value = on ? 1 : 0;
  }

  get reduceFlashes(): boolean {
    return this.reduced;
  }

  /** Uma nuvem de `n` baforadas em volta do ponto (a mais antiga cede lugar se o pool encher). */
  burst(x: number, y: number, z: number, n = 8, spread = 0.5): void {
    const [r, g, b] = SMOKE_GRAY;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      this.smoke.write(this.smoke.push(), x + Math.cos(a) * spread * 0.5, y + (i % 3) * 0.3, z + Math.sin(a) * spread * 0.5, Math.cos(a) * 0.4, 0.8 + (i % 4) * 0.15, Math.sin(a) * 0.4, LIFE, 1, 0.55, r, g, b, PUFF_KIND.fumaca);
    }
  }

  /**
   * Poeira nos pés (Fase 14): `n` sopros em anel em volta do ponto (no chão), abrindo rente ao chão a `speed`
   * m/s; `dx`/`dz` empurram o anel para um lado (o rastro sai para trás de quem corre). `size` é a largura
   * do sopro aberto (m) e `alpha` a força (os efeitos dos aliados vêm mais fracos). No draw da fumaça.
   */
  dust(x: number, y: number, z: number, n = 6, size = 0.7, speed = 1.6, alpha = 0.5, dx = 0, dz = 0): void {
    if (alpha <= 0) return;
    const [r, g, b] = DUST_COLOR;
    for (let i = 0; i < n; i++) {
      const k = this.seq++;
      const a = k * GOLDEN;
      const j = ((k * 7919) % 97) / 97;
      const v = speed * (0.6 + 0.4 * j);
      this.smoke.write(this.smoke.push(), x + Math.cos(a) * 0.15, y + 0.12 + 0.1 * j, z + Math.sin(a) * 0.15, Math.cos(a) * v + dx, 0.25 + 0.3 * j, Math.sin(a) * v + dz, DUST_LIFE * (0.7 + 0.3 * j), size * (0.75 + 0.5 * j), alpha, r, g, b, PUFF_KIND.poeira);
    }
  }

  /**
   * `n` lambidas de chama na cor dada, espalhadas até `spread` m em volta do ponto (no chão), subindo.
   * `size`: a largura da língua (m); `alpha`: a força (os efeitos dos aliados vêm mais fracos).
   */
  flames(x: number, y: number, z: number, color: string, n = 3, spread = 0.3, size = 0.55, alpha = 1): void {
    if (alpha <= 0) return;
    const c = this.color(color);
    for (let i = 0; i < n; i++) {
      const k = this.seq++;
      const a = k * GOLDEN;
      const j = ((k * 7919) % 97) / 97;
      const d = spread * Math.sqrt((i + 0.5) / n);
      this.glow.write(this.glow.push(), x + Math.cos(a) * d, y, z + Math.sin(a) * d, Math.cos(a) * 0.15, 0.7 + 0.5 * j, Math.sin(a) * 0.15, FLAME_LIFE * (0.7 + 0.3 * j), size * (0.8 + 0.4 * j), alpha, c.r, c.g, c.b, PUFF_KIND.chama + seed(k));
    }
    this.glow.mesh.visible = true;
  }

  /** `n` faíscas de magia na cor dada, abrindo em volta do ponto (o acerto do cajado). */
  magic(x: number, y: number, z: number, color: string, n = 5, size = 0.35, alpha = 1): void {
    if (alpha <= 0) return;
    const c = this.color(color);
    for (let i = 0; i < n; i++) {
      const k = this.seq++;
      const a = k * GOLDEN;
      const j = ((k * 7919) % 97) / 97;
      // Direção na esfera (Fibonacci), puxada para cima.
      const yy = 1 - 1.4 * ((i + 0.5) / n);
      const r = Math.sqrt(Math.max(0, 1 - yy * yy));
      const v = 1.6 + 1.2 * j;
      this.glow.write(this.glow.push(), x, y, z, Math.cos(a) * r * v, yy * v, Math.sin(a) * r * v, MAGIC_LIFE * (0.7 + 0.3 * j), size * (0.7 + 0.6 * j), alpha, c.r, c.g, c.b, PUFF_KIND.magia + seed(k));
    }
    this.glow.mesh.visible = true;
  }

  update(dt: number): void {
    this.time += dt;
    this.smoke.update(dt);
    this.glow.update(dt);
    this.glow.mesh.visible = this.glow.live > 0;
    if (this.marks.mesh.visible) {
      this.marks.update(dt);
      this.marks.mesh.visible = this.marks.live > 0;
    }
    // O relógio das chamas: os dois materiais dividem o programa, então os dois levam o mesmo instante.
    this.smoke.material.uniforms["uTime"]!.value = this.time;
    this.glow.material.uniforms["uTime"]!.value = this.time;
  }

  /** Sopros de poeira vivos (Fase 14). */
  get dustLive(): number {
    return this.smoke.countKind(PUFF_KIND.poeira);
  }

  /** Ondas na água e marcas de lama vivas (diagnóstico). */
  get marksLive(): { ripples: number; marks: number } {
    return { ripples: this.marks.countKind(PUFF_KIND.onda), marks: this.marks.countKind(PUFF_KIND.marca) };
  }

  /** Baforadas de fumaça vivas. */
  get count(): number {
    return this.smoke.live;
  }

  /** Chamas e magia vivas, e se o draw delas está ligado (diagnóstico e e2e). */
  get stats(): { smoke: number; flames: number; magic: number; capacity: number; visible: boolean } {
    return { smoke: this.smoke.live, flames: this.glow.countKind(PUFF_KIND.chama), magic: this.glow.countKind(PUFF_KIND.magia), capacity: GLOW_POOL, visible: this.glow.mesh.visible };
  }

  /** Chamas e magia vivas (o teto das lambidas sobre os perigos e a Fênix lê isso). */
  get glowLive(): number {
    return this.glow.live;
  }

  /** As malhas (os testes conferem a assinatura do programa e o material). */
  get meshes(): { smoke: Mesh; glow: Mesh; marks: Mesh } {
    return { smoke: this.smoke.mesh, glow: this.glow.mesh, marks: this.marks.mesh };
  }

  clear(): void {
    this.smoke.clear();
    this.glow.clear();
    this.glow.mesh.visible = false;
    this.marks.clear();
    this.marks.mesh.visible = false;
  }

  dispose(): void {
    this.smoke.dispose();
    this.glow.dispose();
    this.marks.dispose();
    this.plane.dispose();
  }

  private color(hex: string): Color {
    let c = this.colors.get(hex);
    if (!c) {
      c = new Color(hex);
      this.colors.set(hex, c);
    }
    return c;
  }
}

/** A semente da instância (0 a 0,9), na fração do `aKind`: cada lambida com o próprio ruído. */
function seed(k: number): number {
  return ((k * 0.618034) % 1) * 0.9;
}
